import React, { useState, useRef, useEffect } from "react";
import { Logo } from "@/components/Logo";
import { useLang, LANGS } from "@/i18n";
import { Instagram, Globe, Check } from "lucide-react";

const LangSelector = () => {
  const { t, lang, changeLang, autoLang } = useLang();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const current = LANGS.find((l) => l.code === lang);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  return (
    <div className="relative" ref={ref} data-testid="lang-selector">
      <button
        data-testid="lang-selector-btn"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 text-xs text-white/50 hover:text-white transition-colors border border-white/10 hover:border-white/25 rounded-full px-3.5 py-1.5"
      >
        <Globe size={14} />
        <span>{current ? current.label : lang.toUpperCase()}</span>
      </button>

      {open && (
        <div className="absolute right-0 bottom-full mb-2 w-48 max-h-72 overflow-auto bg-[#111111] border border-white/10 rounded-xl py-1.5 shadow-2xl z-30" data-testid="lang-selector-menu">
          <button
            data-testid="lang-opt-auto"
            onClick={() => { autoLang(); setOpen(false); }}
            className="w-full flex items-center gap-2 text-left px-3.5 py-2 text-sm text-white/70 hover:bg-white/5 hover:text-white transition-colors"
          >
            <Globe size={13} /> {t.footer.auto}
          </button>
          <div className="my-1 h-px bg-white/5" />
          {LANGS.map((l) => (
            <button
              key={l.code}
              data-testid={`lang-opt-${l.code}`}
              onClick={() => { changeLang(l.code); setOpen(false); }}
              className={`w-full flex items-center justify-between text-left px-3.5 py-2 text-sm hover:bg-white/5 transition-colors ${l.code === lang ? "text-[#d4af37]" : "text-white/70 hover:text-white"}`}
            >
              {l.label}
              {l.code === lang && <Check size={13} />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export const Footer = () => {
  const { t } = useLang();
  return (
    <footer data-testid="footer" className="border-t border-white/10 bg-[#050505] py-14">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 flex flex-col md:flex-row items-center justify-between gap-8">
        <div className="flex flex-col items-center md:items-start gap-3">
          <Logo />
          <p data-testid="footer-slogan" className="text-sm font-serif-lux italic text-[#d4af37]/80 font-light">{t.footer.tagline}</p>
        </div>
        <div className="flex items-center gap-5 text-white/50">
          <a href="https://www.instagram.com/lumin_africa/" target="_blank" rel="noreferrer" aria-label="Instagram" data-testid="footer-instagram" className="hover:text-[#d4af37] transition-colors"><Instagram size={18} /></a>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-6 lg:px-8 mt-10 pt-6 border-t border-white/5 flex flex-col sm:flex-row items-center justify-between gap-4">
        <p className="text-xs text-white/30 order-2 sm:order-1">
          © {new Date().getFullYear()} Lumina Africa. {t.footer.rights}
        </p>
        <div className="order-1 sm:order-2">
          <LangSelector />
        </div>
      </div>
    </footer>
  );
};
